import Dropzone from "dropzone";
import { Controller } from "stimulus";
import { DirectUpload } from "@rails/activestorage";
import SparkMD5 from "spark-md5";
import {
  getMetaValue,
  findElement,
  removeElement,
  insertAfter,
} from "../helpers/dropzone_helpers";
import dotenv from "dotenv";

// Load environment variables from .env file
dotenv.config();
export default class extends Controller {
  static targets = ["input", "loadingImage"];
  fileSize = [];
  connect() {
    if (this.hasLoadingImageTarget) {
      this.hideLoader();
    }
    this.dropZone = createDropZone(this);
    this.hideFileInput();
    this.bindEvents();
    window.uploadImageCounter = 0;
    window.responseCounter = 0;
    window.fetchResponses = [];
    Dropzone.autoDiscover = false;
    this.nodeURL = this.element.dataset.uploadUrl;
    this.boundVisitChangeHandler = this.syncVisitGatedUpload.bind(this);
    this.setupVisitGatedUpload();
    // Listen for the event
    this.element.addEventListener(
      "successFileUpload",
      this.handleFileUploadSuccess.bind(this)
    );

    this.element.addEventListener("dragover", (event) => {
      event.preventDefault(); // Necessary for drop to work
    });

    this.element.addEventListener("drop", (event) => {
      event.preventDefault();
      if (this.isUploadDisabledByVisit()) return;
      const imageUrl = event.dataTransfer.getData("text/plain");

      if (imageUrl) {
        this.uploadImageFromUrl(imageUrl);
      }
    });
  }

  disconnect() {
    const visitSelect = this.visitSelectElement();
    if (visitSelect && this.boundVisitChangeHandler) {
      visitSelect.removeEventListener("change", this.boundVisitChangeHandler);
    }
  }

  requiresVisit() {
    return this.element.dataset.dropzoneRequireVisit === "true";
  }

  visitSelectElement() {
    return (
      this.element.closest("[data-controller~='stack']")?.querySelector(
        'select[name="stack[visit_id]"]'
      ) || document.querySelector('select[name="stack[visit_id]"]')
    );
  }

  selectedVisitName() {
    const visitSelect = this.visitSelectElement();
    if (!visitSelect || visitSelect.selectedIndex < 0) return null;

    const option = visitSelect.options[visitSelect.selectedIndex];
    if (!option || !option.value) return null;

    return option.text.trim();
  }

  isUploadDisabledByVisit() {
    return this.requiresVisit() && !this.selectedVisitName();
  }

  setupVisitGatedUpload() {
    if (!this.requiresVisit()) return;

    const visitSelect = this.visitSelectElement();
    if (visitSelect) {
      visitSelect.addEventListener("change", this.boundVisitChangeHandler);
    }
    this.syncVisitGatedUpload();
  }

  syncVisitGatedUpload() {
    if (!this.requiresVisit() || !this.dropZone) return;

    const visitHint = this.element.querySelector(".dropzone-msg-visit-hint");
    if (this.isUploadDisabledByVisit()) {
      this.dropZone.disable();
      this.element.classList.add("opacity-60", "cursor-not-allowed");
      if (visitHint) visitHint.classList.remove("hidden");
    } else {
      this.dropZone.enable();
      this.element.classList.remove("opacity-60", "cursor-not-allowed");
      if (visitHint) visitHint.classList.add("hidden");
    }
  }

  buildStackStorageContext() {
    if (!this.requiresVisit()) return null;

    const visitName = this.selectedVisitName();
    const studyCode = this.element.dataset.dropzoneStudyCode;
    const siteCode = this.element.dataset.dropzoneSiteCode;
    const subjectCode = this.element.dataset.dropzoneSubjectCode;

    if (!visitName || !studyCode || !siteCode || !subjectCode) return null;

    return {
      study_id: this.element.dataset.dropzoneStudyId,
      study_code: studyCode,
      site_code: siteCode,
      subject_code: subjectCode,
      visit_name: visitName,
    };
  }

  studyUploadContext() {
    const studyId = this.element.dataset.dropzoneStudyId;
    const studyCode = this.element.dataset.dropzoneStudyCode;
    const context = {};
    if (studyId) context.studyId = studyId;
    if (studyCode) context.studyCode = studyCode;
    return context;
  }

  showLoader() {
    this.loadingImageTarget.classList.add("visible");
  }

  hideLoader() {
    this.loadingImageTarget.classList.remove("visible");
  }

  async uploadImageFromUrl(imageUrl) {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], "dropped-image.jpg", { type: blob.type });

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);

      this.inputTarget.files = dataTransfer.files;

      this.uploadFile(file);
    } catch (error) {
      console.error("Error fetching image:", error);
    }
  }

  uploadFile(file) {
    const upload = new DirectUpload(
      file,
      this.inputTarget.dataset.directUploadUrl
    );

    upload.create((error, blob) => {
      if (error) {
        console.error("Direct upload error:", error);
      } else {
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("value", blob.signed_id);
        hiddenField.setAttribute("name", this.attachmentFieldName());

        this.element.appendChild(hiddenField);
      }
    });
  }

  handleFileUploadSuccess(event) {
    const { fileName, questionName, file } = event.detail;
    const images = document.querySelectorAll('.attached_images');
    const values = Array.from(images).map(image => image.value); 
    const lastImageValue = images[images.length - 1]?.value;
    const signed_id = lastImageValue
    // Perform any actions with the file
    const studyId = this.element.dataset.dropzoneStudyId;    
    const subjectId = this.element.dataset.dropzoneSubjectId;
    const isUploadXmlFlag =
      this.element.dataset.dropzoneUploadXmlFlag === "true";
    this.processFile(file);
    const railsEnv = this.getRailsEnv();
    const skipMetadata = this.element.dataset.dropzoneSkipMetadata === "true";
    if (railsEnv !== "production" && !skipMetadata) {
      if (
        file.name.toLowerCase().endsWith(".dcm") ||
        file.name.toLowerCase().endsWith(".dicom")
      ) {
        this.getMetadata(file);
      }

    }
    if (isUploadXmlFlag) {
      this.extractMetadata(file, studyId, subjectId);
    }
    if (!signed_id) {
    console.warn("signed_id not available yet");
    return;
  }

  const subjectCode = this.element.dataset.dropzoneSubjectCode;
   const visitDateInput = document.querySelector('input[data-visitdate="true"]');
   const packageVerificationInput = document.querySelector('input[name="stack[package_verification]"]:checked')
 const packageTypeSelect = document.querySelector('select[name="stack[package_type]"]');
 const visitDate = visitDateInput?.value;
 const visitTypeSelect = document.querySelector('select[name="stack[visit_id]"]');
 const visitText = visitTypeSelect
  ? visitTypeSelect.options[visitTypeSelect.selectedIndex].text
  : null;
  const packageVerificationcheck = packageVerificationInput?.value;
  const packageType = packageTypeSelect?.value;
const isPackageVerified = packageVerificationcheck === "true";
  const shouldCallApi =
    this.element.dataset.dropzoneApiMetadata === "true";

  if (shouldCallApi && visitText && visitText == "System Visit"){
   this.extractStimiliData(values,subjectCode);
  }
 if (shouldCallApi && isPackageVerified) {
   if (!this.isMetadataSupportedFile(file?.name || fileName)) {
    console.log(fileName);
    console.log(file?.name);
    
     Swal.fire({
       icon: "error",
       title: "Unsupported File Type",
       text: "Package verification is only supported for .e2e, .ex.dcm, .oct, .fda, .tgz, and .dcm files.",
       confirmButtonText: "OK",
     });
     return;
   }

   Swal.fire({
    title: "Verifying Package",
    text: "Please wait while we verify the uploaded file…",
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });
  this.sendToRailsForMetadata(signed_id, subjectCode, visitDate, packageVerificationcheck, packageType);
  }

  }

static METADATA_SUPPORTED_EXTENSIONS = [".e2e", ".ex.dcm", ".oct", ".fda", ".tgz", ".dcm"];

isMetadataSupportedFile(fileName) {
  const normalizedFileName = String(fileName || "").toLowerCase();

  return this.constructor.METADATA_SUPPORTED_EXTENSIONS.some((extension) =>
    normalizedFileName.endsWith(extension)
  );
}

normalizeMetadataValue(value) {
  return String(value || "").trim().toLowerCase();
}

getSelectDisplayValue(selector) {
  const select = document.querySelector(selector);
  if (!select || select.selectedIndex < 0) return "";

  const selectedOption = select.options[select.selectedIndex];
  if (!selectedOption || selectedOption.value === "") return "";

  return selectedOption.text.trim();
}

getFormSerialNumber() {
  const serialSelect = document.querySelector(
    'select[name*="device_serial_number"]'
  );
  const serialInput = document.querySelector(
    'input[name*="device_serial_number_input"]'
  );
  const selectedSerial = serialSelect?.value;

  if (selectedSerial === "Others" || selectedSerial === "Uncertified Device") {
    return serialInput?.value?.trim() || "";
  }

  return selectedSerial?.trim() || "";
}

extractMetadataSerialNumbers(metadataItem) {
  const serialEntries = metadataItem["Device OIRRC ID or Serial Number"] || [];

  return serialEntries.flatMap((entry) =>
    Object.values(entry || {})
      .map((value) => String(value || "").trim())
      .filter(Boolean)
  );
}

buildMetadataMismatchTable(mismatchRows) {
  const tableRows = mismatchRows
    .map(
      ({ field, formValue, extractedValue }) => `
        <tr>
          <td style="padding:10px 12px; border:1px solid #e5e7eb; font-weight:600; background-color:#fef2f2; color:#991b1b; white-space:nowrap;">${field}</td>
          <td style="padding:10px 12px; border:1px solid #e5e7eb; word-break:break-word;">${formValue || "N/A"}</td>
          <td style="padding:10px 12px; border:1px solid #e5e7eb; word-break:break-word;">${extractedValue || "N/A"}</td>
        </tr>
      `
    )
    .join("");

  return `
    <div style="overflow-x:auto; text-align:left;">
      <table style="width:100%; border-collapse:collapse; font-size:14px;">
        <thead>
          <tr style="background-color:#f3f4f6;">
            <th style="padding:10px 12px; border:1px solid #e5e7eb; text-align:left;">Field</th>
            <th style="padding:10px 12px; border:1px solid #e5e7eb; text-align:left;">Form Value</th>
            <th style="padding:10px 12px; border:1px solid #e5e7eb; text-align:left;">Extracted Value</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    </div>
  `;
}

sendToRailsForMetadata(signedId, subjectCode, visitDate, packageVerificationcheck, packageType) {
  fetch("/api_extracted_metadata", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
    },
    body: JSON.stringify({
      signed_id: signedId
    })
  })
    .then(res => res.json())
    .then(data => {
      Swal.close(); // 🔥 CLOSE LOADER HERE
      const metadata = data.extracted_metadata;  
      if (!metadata) {
        Swal.fire({
          icon: "error",
          title: "Metadata Error",
          text: "Failed to extract metadata from the uploaded file."
        });
        return;
      }
     
      const metadataItem = metadata.metadata?.[0] || {};

      const {
        Subject_ID,
        Study_Eye,
        Visit_Date,
        Modality,
        Child_Modality,
        Manufacturer
      } = metadataItem;

      const extractedSerialNumbers = this.extractMetadataSerialNumbers(metadataItem);
      const formModality = this.getSelectDisplayValue('select[name="stack[modality_id]"]');
      const formChildModality = this.getSelectDisplayValue('select[name="stack[child_modality_id]"]');
      const formManufacturer =
        document.querySelector('input[name*="device_manufacturer"]')?.value?.trim() || "";
      const formSerialNumber = this.getFormSerialNumber();
  
      const backendSubjectId = String(Subject_ID);
      const backendVisitDate =
  `${Visit_Date.slice(0,4)}-${Visit_Date.slice(4,6)}-${Visit_Date.slice(6,8)}`;
      const backendEyeLaterality = Study_Eye;
      const backendModality = Modality || "";
      const backendChildModality = Child_Modality || "";
      const backendManufacturer = Manufacturer || "";
      const backendSerialNumbersDisplay = extractedSerialNumbers.join(", ") || "N/A";

      const subjectMismatch = backendSubjectId !== String(subjectCode);
      const visitDateMismatch = backendVisitDate !== visitDate;
      const eyeLateralityMismatch = backendEyeLaterality !== packageType;
      const modalityMismatch =
        backendModality &&
        this.normalizeMetadataValue(backendModality) !== this.normalizeMetadataValue(formModality);
      const childModalityMismatch =
        backendChildModality &&
        this.normalizeMetadataValue(backendChildModality) !==
          this.normalizeMetadataValue(formChildModality);
      const manufacturerMismatch =
        backendManufacturer &&
        this.normalizeMetadataValue(backendManufacturer) !==
          this.normalizeMetadataValue(formManufacturer);
      const serialMismatch =
        extractedSerialNumbers.length > 0 &&
        formSerialNumber &&
        !extractedSerialNumbers.some(
          (serial) =>
            this.normalizeMetadataValue(serial) ===
            this.normalizeMetadataValue(formSerialNumber)
        );

      const hasMismatch =
        subjectMismatch ||
        visitDateMismatch ||
        eyeLateralityMismatch ||
        modalityMismatch ||
        childModalityMismatch ||
        manufacturerMismatch ||
        serialMismatch;

    if (hasMismatch) {
      const mismatchRows = [];

      if (subjectMismatch) {
        mismatchRows.push({
          field: "Subject ID",
          formValue: subjectCode,
          extractedValue: backendSubjectId,
        });
      }

      if (visitDateMismatch) {
        mismatchRows.push({
          field: "Visit Date",
          formValue: visitDate,
          extractedValue: backendVisitDate,
        });
      }

      if (eyeLateralityMismatch) {
        mismatchRows.push({
          field: "Eye Laterality",
          formValue: packageType,
          extractedValue: backendEyeLaterality,
        });
      }

      if (modalityMismatch) {
        mismatchRows.push({
          field: "Modality",
          formValue: formModality,
          extractedValue: backendModality,
        });
      }

      if (childModalityMismatch) {
        mismatchRows.push({
          field: "Child Modality",
          formValue: formChildModality,
          extractedValue: backendChildModality,
        });
      }

      if (manufacturerMismatch) {
        mismatchRows.push({
          field: "Manufacturer",
          formValue: formManufacturer,
          extractedValue: backendManufacturer,
        });
      }

      if (serialMismatch) {
        mismatchRows.push({
          field: "Serial Number",
          formValue: formSerialNumber,
          extractedValue: backendSerialNumbersDisplay,
        });
      }

      Swal.fire({
        icon: "warning",
        title: "Package Verification",
        html: this.buildMetadataMismatchTable(mismatchRows),
        confirmButtonText: "OK",
        width: "720px",
      });


      } else {
        Swal.fire({
          icon: "success",
          title: "Package Verified",
          text: "Package verification completed successfully."
        });

        // ✅ continue workflow here
      }
    })
    .catch(err => {
       Swal.close(); // 🔥 CLOSE LOADER ON ERROR
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Unexpected Error",
        text: "Something went wrong while validating metadata."
      });
    });
}

  getRailsEnv() {
    const meta = document.querySelector('meta[name="rails-env"]');
    return meta ? meta.content : null;
  }

  // Enhanced JavaScript function with database lookup
  getMetadata(file) {
    const formData = new FormData();
    formData.append("uploaded_file", file);

    fetch("/parse_metadata", {
      method: "POST",
      headers: {
        "X-CSRF-Token": getMetaValue("csrf-token"),
      },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Parsed metadata:", data);
        // Check if site is in database
        this.checkSiteInDatabase(data);
        // Now check if device exists in database
        this.checkDeviceInDatabase(data);
      })
      .catch((err) => console.error("Error extracting metadata", err));
  }

  checkSiteInDatabase(metaData) {
    fetch("/check_site", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getMetaValue("csrf-token"),
      },
      body: JSON.stringify({
        site_name: metaData.institution_name,
      }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.site_found) {
          // Site exists in database - populate from DB record
          this.populateSiteFromDatabase(response.site, metaData);
        }
      });
  }
  // New method to check device in database and populate form
  checkDeviceInDatabase(metaData) {
    fetch("/check_device", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getMetaValue("csrf-token"),
      },
      body: JSON.stringify({
        model_name: metaData.model_name,
      }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.device_found) {
          // Device exists in database - populate from DB record
          this.populateFormFromDatabase(response.device, metaData);
        } else {
          // Device not found - use metadata values as before
          // this.populateFormFromMetadata(metaData);
          alert("Device not found in database");
        }
      })
      .catch((err) => {
        console.error("Error checking device in database", err);
        // Fallback to metadata population
        this.populateFormFromMetadata(metaData);
      });
  }

  // Populate form using database record
  populateSiteFromDatabase(siteRecord) {
    const siteDropdown = document.getElementById(
      "certification_request_site_id"
    );
    this.setDropdownValue(siteDropdown, siteRecord.id, siteRecord.name);
  }
  populateFormFromDatabase(deviceRecord, metaData) {
    const deviceDropdown = document.getElementById(
      "certification_request_device_id"
    );
    const serialNumber = document.getElementById(
      "certification_request_old_serial_number"
    );
    const softwareVersion = document.getElementById(
      "certification_request_old_software_version"
    );

    const dateInput = document.getElementById(
      "certification_request_date_of_capture"
    );

    if (dateInput && metaData?.acquisition_date) {
      const rawDate = metaData.acquisition_date.toString();

      // Format from YYYYMMDD → YYYY-MM-DD
      const formattedDate = `${rawDate.slice(0, 4)}-${rawDate.slice(
        4,
        6
      )}-${rawDate.slice(6, 8)}`;

      dateInput.value = formattedDate;
    }

    // Set device dropdown to the UUID from database
    this.setDropdownValue(deviceDropdown, deviceRecord.id, deviceRecord.name);

    // Use database values for other fields
    this.setDropdownValue(
      serialNumber,
      deviceRecord.serial_no,
      deviceRecord.serial_no
    );
    this.setDropdownValue(
      softwareVersion,
      deviceRecord.model_versions,
      deviceRecord.model_versions
    );

    console.log("Form populated from database record:", deviceRecord);
  }

  // Populate form using metadata (fallback or when device not found)
  populateFormFromMetadata(metaData) {
    const deviceDropdown = document.getElementById(
      "certification_request_device_id"
    );
    const serialNumber = document.getElementById(
      "certification_request_old_serial_number"
    );
    const softwareVersion = document.getElementById(
      "certification_request_old_software_version"
    );

    // Use metadata values as before
    this.setDropdownValue(
      deviceDropdown,
      metaData.model_name,
      metaData.model_name
    );
    this.setDropdownValue(
      serialNumber,
      metaData.serial_number,
      metaData.serial_number
    );
    this.setDropdownValue(
      softwareVersion,
      metaData.software_version,
      metaData.software_version
    );

    console.log("Form populated from metadata:", metaData);
  }

  // Enhanced function to set dropdown values with separate value and display text
  setDropdownValue(dropdown, value, displayText = null) {
    let optionExists = false;
    const textToShow = displayText || value;

    for (let option of dropdown.options) {
      if (option.value === value) {
        option.selected = true;
        optionExists = true;
        break;
      }
    }

    // If the value doesn't exist, create a new option
    if (!optionExists) {
      const newOption = document.createElement("option");
      newOption.value = value;
      newOption.text = textToShow;
      newOption.selected = true;
      dropdown.appendChild(newOption);
    }
  }

  extractMetadata(file, studyId, subjectId) {
    const formData = new FormData();
    formData.append("uploaded_file", file);
    formData.append("study_id", studyId);
    formData.append("subject_id", subjectId);
    fetch("/parse_device_metadata", {
      method: "POST",
      headers: {
        "X-CSRF-Token": getMetaValue("csrf-token"),
      },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Parsed metadata:", data);
        if (data.success && data.redirect_url) {
          window.location.href = data.redirect_url;
          this.fillFormFields?.(data.metadata);
        } else {
          Swal.fire({
            icon: "error",
            title: "Upload Failed",
            text: data.error || "An unknown error occurred.",
            confirmButtonText: "OK",
          }).then(() => {
            window.location.reload(); // 🔄 Reload even on error
          });
        }
      })
      .catch((err) => {
        console.error("Error extracting metadata", err);
        Swal.fire({
          icon: "error",
          title: "Upload Error",
          text: "An error occurred while uploading or parsing the file.",
          confirmButtonText: "OK",
        }).then(() => {
          window.location.reload(); // 🔄 Reload even on catch
        });
      })
      .finally(() => {
        this.hideLoader(); // ✅ Always called, success or error
      });
  }

extractStimiliData(values, subjectCode) {
  fetch("/extract_mp_data", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": getMetaValue("csrf-token"),
    },
    body: JSON.stringify({ values, subjectCode }),
  })
    .then(res => res.json())
    .then(data => {

      if (!data.success) {

        let message = "";

        const hasSubjectMismatch =
          data.errors && data.errors.includes("subject_mismatch");

        const hasSameLaterality =
          data.errors && data.errors.includes("same_laterality");

        // ✅ Error messages (top)
        if (hasSubjectMismatch) {
          message += `
            <div style="color:#c0392b; margin-bottom:10px;">
              <b>
              Subject code in this XML does not correspond to the system subject code (${subjectCode}).
              </b>
            </div>
          `;
        }

        if (hasSameLaterality) {
          message += `
            <div style="color:#c0392b; margin-bottom:15px;">
              <b>
              Laterality is the same for uploaded XML files. This should not happen, please check before continue.
              </b>
            </div>
          `;
        }

        message += `<hr>`;

        // ✅ File details
        data.files.forEach((file, index) => {

          // remove extension
          const filenameWithoutExt = file.filename.replace(/\.[^/.]+$/, "");

          message += `<div style="text-align:left; margin-bottom:15px;">`;
          message += `<b>For file ${index + 1} (${filenameWithoutExt}):</b><br>`;

          if (hasSameLaterality) {
            message += `Laterality: ${file.eye || "N/A"}<br>`;
          }

          if (hasSubjectMismatch) {
            message += `SubjectID: ${file.patient_id || "N/A"}<br>`;
          }

          message += `</div>`;
        });

        Swal.fire({
          icon: "warning",
          title: "Validation Alert",
          html: message,
          confirmButtonText: "OK",
          width: 650
        });

        return;
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "XML files validated successfully."
      });

    })
    .catch(() => {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong while processing XML files."
      });
    });
}
  processFile(file) {
    // Example: Preview, process, or store the file
  }

  // Private
  hideFileInput() {
    this.inputTarget.disabled = true;
    this.inputTarget.style.display = "none";
  }

  bindEvents() {
    this.dropZone.on("addedfile", (file) => {
      if (this.isUploadDisabledByVisit()) {
        this.dropZone.removeFile(file);
        Swal.fire({
          icon: "warning",
          title: "Select a Visit",
          text: "Please select a Visit before uploading files.",
          confirmButtonText: "OK",
        });
        return;
      }
      if (this.element.dataset.dropzoneUploadXmlFlag === "true") {
        document.querySelectorAll(".dz-preview")[0].remove();
        this.showLoader();
      }
      // Check file size
      const maxFileSizeMB = 256; // 256 MB
      const actualSize = file.size / (1024 * 1024);
      this.fileSize.push(parseInt(actualSize));
      const hasSmallFile = this.fileSize.some((size) => size < maxFileSizeMB);
      if (hasSmallFile) {
        this.dropZone.options.dictCancelUploadConfirmation =
          "Are you sure you want to cancel the upload? By doing this, all of your other files whose size is less than 256 MB will be canceled.";
      } else {
        this.dropZone.options.dictCancelUploadConfirmation =
          "Are you sure you want to cancel this upload?";
      }
      if (file.size > maxFileSizeMB * 1024 * 1024) {
        // Call handleLargeFileUpload for files larger than 256 MB
        handleLargeFileUpload(this, file, this.nodeURL);
      } else {
        setTimeout(() => {
          file.accepted && createDirectUploadController(this, file).start();
        }, 500);
      }
    });

    this.dropZone.on("removedfile", async (file) => {
      if (file.isCanceled) {
        // Skip the removedfile logic if the file was canceled
        return;
      }

      // QMS "Add Category" copies signed_ids to the pending card, then clears this
      // dropzone. Must not delete Azure bytes — the pending card still needs them.
      if (file.skipRemoteDelete) {
        if (file.controller?.hiddenInput) removeElement(file.controller.hiddenInput);
        else if (file.hiddenInput) removeElement(file.hiddenInput);
        this.emitRemoveEvent(file);
        return;
      }

      if (file.controller) {
        const responseUrl = file.controller.xhr && file.controller.xhr.responseURL;
        if (responseUrl) {
          const selectChangeUrl =
            document.location.origin + `/remove_canceled_file.json`;
          fetch(selectChangeUrl, {
            headers: {
              "Content-Type": "application/json; charset=utf-8",
              "X-CSRF-Token": document.head
                .querySelector('meta[name="csrf-token"]')
                .getAttribute("content"),
            },
            body: JSON.stringify({ response: responseUrl }),
            method: "POST",
          })
            .then((response) => response.json())
            .then((data) => {
              console.log(data.message);
            })
            .catch(() => {});
        }
        removeElement(file.controller.hiddenInput);
      } else if (file.uploadUrl) {
        await deleteBlobFromAzure(file.uploadUrl);
        removeElement(file.hiddenInput);
      }
      this.emitRemoveEvent(file);
    });

    this.dropZone.on("canceled", (file) => {
      file.isCanceled = true;
      if (file.controller) {
        file.controller.xhr.abort();
        removeElement(file.controller.hiddenInput);
      } else if (file.xhr) {
        file.xhr.abort();
        removeElement(file.hiddenInput);
      }
    });

    this.dropZone.on("success", (file) => {
      this.emitSuccessEvent(file);
    });
  }

  emitSuccessEvent(file) {
    const dataType = this.inputTarget.getAttribute("data-type");
    const event = new CustomEvent("successFileUpload", {
      detail: {
        message: "Hello from Sender Controller",
        fileName: file.name,
        questionName: this.inputTarget.getAttribute("data-question"),
        eyeSection: this.inputTarget.getAttribute("data-eye"),
        modality: this.inputTarget.getAttribute("data-modality"),
        file, // Include the file object here
        dropzone: this.dropZone,
        dataType: dataType,
      },
      bubbles: true,
    });
    this.element.dispatchEvent(event);
    if (!dataType) {
      window.dispatchEvent(event); // Ensure global dispatch includes full detail
    }
  }

  emitRemoveEvent(file) {
    const dataType = this.inputTarget.getAttribute("data-type");
    const event = new CustomEvent("removeFile", {
      detail: {
        message: "Hello from Sender Controller",
        fileName: file.name,
        questionName: this.inputTarget.getAttribute("data-question"),
        eyeSection: this.inputTarget.getAttribute("data-eye"),
        modality: this.inputTarget.getAttribute("data-modality"),
        file, // Include the file object here
        dropzone: this.dropZone,
        dataType: dataType,
      },
      bubbles: true,
    });
    this.element.dispatchEvent(event);
    // Dispatch the same event to the window object
    window.dispatchEvent(event);
  }
  get headers() {
    return { "X-CSRF-Token": getMetaValue("csrf-token") };
  }

  get url() {
    return this.inputTarget.getAttribute("data-direct-upload-url");
  }

  get maxFiles() {
    return this.data.get("maxFiles") || 10;
  }

  get maxFileSize() {
    return this.data.get("maxFileSize") || 5120;
  }

  get acceptedFiles() {
    return this.data.get("acceptedFiles");
  }

  get addRemoveLinks() {
    return this.data.get("addRemoveLinks") || true;
  }

  get form() {
    return this.element.closest("form");
  }

  attachmentFieldName() {
    return (
      this.inputTarget.getAttribute("data-qms-attachment-name") ||
      this.inputTarget.name
    );
  }

  get submitButton() {
    return findElement(this.form, "input[type=submit], button[type=submit]");
  }

  bindProgressEvent(xhr, file) {
    xhr.upload.addEventListener("progress", (event) =>
      this.uploadRequestDidProgress(event, file)
    );
  }

  uploadRequestDidProgress(event, file) {
    const progress = (event.loaded / event.total) * 100;
    findElement(
      file.previewTemplate,
      ".dz-upload"
    ).style.width = `${progress}%`;
  }

  emitDropzoneUploading(file) {
    file.status = Dropzone.UPLOADING;
    this.dropZone.emit("processing", file);
  }

  emitDropzoneError(file, error) {
    file.status = Dropzone.ERROR;
    this.dropZone.emit("error", file, error);
    this.dropZone.emit("complete", file);
  }

  emitDropzoneSuccess(file) {
    file.status = Dropzone.SUCCESS;
    this.dropZone.emit("success", file);
    this.dropZone.emit("complete", file);
  }
}

class DirectUploadController {
  constructor(source, file) {
    this.directUpload = createDirectUpload(file, source.url, this);
    this.source = source;
    this.file = file;
    this.iolUrl = this.source.element.dataset.dropzoneIolUrl;
  }

  start() {
    this.file.controller = this;
    this.hiddenInput = this.createHiddenInput();
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput);
        this.emitDropzoneError(error);
      } else {
        this.hiddenInput.value = attributes.signed_id;
        this.emitDropzoneSuccess(attributes, this.iolUrl);
      }
    });
  }

  createHiddenInput() {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = this.source.attachmentFieldName
      ? this.source.attachmentFieldName()
      : this.source.inputTarget.name;
    input.classList.add("attached_images");
    insertAfter(input, this.source.inputTarget);
    return input;
  }

  directUploadWillCreateBlobWithXHR(xhr) {
    const stackStorage = this.source.buildStackStorageContext?.();
    if (!stackStorage) return;

    const originalSend = xhr.send.bind(xhr);
    xhr.send = (body) => {
      try {
        const payload = JSON.parse(body);
        payload.blob = payload.blob || {};
        payload.blob.stack_storage = stackStorage;
        originalSend(JSON.stringify(payload));
      } catch (_error) {
        originalSend(body);
      }
    };
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.bindProgressEvent(xhr);
    this.emitDropzoneUploading();
  }

  bindProgressEvent(xhr) {
    this.xhr = xhr;
    this.xhr.upload.addEventListener("progress", (event) =>
      this.uploadRequestDidProgress(event)
    );
  }

  uploadRequestDidProgress(event) {
    const element = this.source.element;
    const progress = (event.loaded / event.total) * 100;
    findElement(
      this.file.previewTemplate,
      ".dz-upload"
    ).style.width = `${progress}%`;
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING;
    this.source.dropZone.emit("processing", this.file);
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR;
    this.source.dropZone.emit("error", this.file, error);
    this.source.dropZone.emit("complete", this.file);
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS;
    this.source.dropZone.emit("success", this.file);
    this.source.dropZone.emit("complete", this.file);
    //allowStackCreate(false);
    //callAPIFunction(this.xhr.responseURL, response.id.toString(), apiUrl);
  }
}

function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file);
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller);
}

function createDropZone(controller) {
  const clickableSelector = controller.element.dataset.dropzoneClickable;
  const options = {
    url: controller.url,
    headers: controller.headers,
    maxFiles: controller.maxFiles,
    maxFilesize: controller.maxFileSize,
    acceptedFiles: controller.acceptedFiles,
    addRemoveLinks: true,
    dictRemoveFile: "Remove file",
    autoQueue: false,
    // Keep previews inside this dropzone (not another zone on the page).
    previewsContainer: controller.element,
  };

  // Dropzone resolves string clickable via document.querySelectorAll — so ".dz-message"
  // would make EVERY dropzone open when ANY message strip is clicked, and uploads
  // appear in the wrong zone. Scope to this element only.
  if (clickableSelector) {
    const localClickables = Array.from(
      controller.element.querySelectorAll(clickableSelector)
    );
    options.clickable = localClickables.length > 0 ? localClickables : true;
  }

  return new Dropzone(controller.element, options);
}

function callAPIFunction(url, id, apiUrl, fetchImageValues) {
  fetch(apiUrl, {
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      "X-CSRF-Token": document.head
        .querySelector('meta[name="csrf-token"]')
        .getAttribute("content"),
    },
    method: "POST",
    body: JSON.stringify({
      image: url,
      models: ["magnification", "focus", "artifacts", "exposure"],
    }),
  })
    .then((response) => {
      return response.json();
    })
    .then((data) => {
      const keyValues = Object.entries(data);
      keyValues.splice(0, 0, ["blob_id", id]);
      const newObj = Object.fromEntries(keyValues);
      window.fetchResponses.push(newObj);
      window.responseCounter += 1;
      if (window.uploadImageCounter <= window.responseCounter) {
        allowStackCreate(true);
      }
    });
}

function allowStackCreate(value) {
  if (value === true) {
    document.getElementById("getResponse").value = JSON.stringify(
      window.fetchResponses
    );
    document.getElementById("otherBtn").style.display = "none";
    document.getElementById("stackBtn").style.display = "block";
  } else if (value === false) {
    document.getElementById("stackBtn").style.display = "none";
    document.getElementById("otherBtn").style.display = "block";
  }
}
async function handleLargeFileUpload(source, file, nodeURL) {
  try {
    // Calculate the checksum using SparkMD5
    const checksum = await calculateChecksum(file);

    // Fetch CSRF token from meta tag
    const csrfToken = document
      .querySelector("meta[name='csrf-token']")
      .getAttribute("content");
    const stackStorage = source.buildStackStorageContext?.();
    // Call the Rails backend to create the blob and get the key
    // This will remain hardcoded, because it will call this URL via internally.
    const blobResponse = await fetch("/active_storage_blobs", {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        blob: {
          filename: file.name,
          byte_size: file.size,
          metadata: {}, // Add any metadata you need
          checksum,
          content_type: file.type,
          ...(stackStorage ? { stack_storage: stackStorage } : {}),
        },
      }),
      credentials: "include", // Ensure cookies are sent with the request
    });

    const blobResponseData = await blobResponse.json();
    const { key: blobName, signed_id: signedID } = blobResponseData;

    // Use the correct reference to create the hidden input and attach it to the file to remove/cancel later
    file.hiddenInput = document.createElement("input");
    file.hiddenInput.type = "hidden";
    file.hiddenInput.name = source.attachmentFieldName
      ? source.attachmentFieldName()
      : source.inputTarget.name;
    insertAfter(file.hiddenInput, source.inputTarget);
    source.hiddenInput = file.hiddenInput;
    // Fetch upload URL
    const upload_url = nodeURL;
    const response = await fetch(upload_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
      body: JSON.stringify({
        blobName,
        ...source.studyUploadContext?.(),
      }),
    });

    const data = await response.json();
    const uploadUrl = data.uploadUrl;

    // Save upload URL to file object
    file.uploadUrl = uploadUrl;

    // Create a new XMLHttpRequest to manually upload the file
    const xhr = new XMLHttpRequest();
    file.xhr = xhr; // Attach xhr to file object
    xhr.open("PUT", uploadUrl, true);
    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");

    // Bind progress events and emit upload events
    source.bindProgressEvent(xhr, file);
    source.emitDropzoneUploading(file);

    // Optionally add event listeners to monitor upload progress
    xhr.upload.addEventListener("progress", (event) => {
      const progress = (event.loaded / event.total) * 100;
    });

    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 201) {
        file.hiddenInput.value = signedID;
        source.emitDropzoneSuccess(file);
      } else {
        console.error("File upload failed.");
        removeElement(file.hiddenInput);
        source.emitDropzoneError(file, xhr.statusText);
      }
    };

    xhr.onerror = () => {
      console.error("File upload error.");
      removeElement(file.hiddenInput);
      source.emitDropzoneError(file, xhr.statusText);
    };

    // Send the file
    xhr.send(file);
  } catch (error) {
    console.error("An error occurred during the file upload process:", error);
    if (file.hiddenInput) {
      removeElement(file.hiddenInput);
    }
    source.emitDropzoneError(file, error.message);
  }
}

async function calculateChecksum(file) {
  return new Promise((resolve, reject) => {
    const chunkSize = 2097152; // Read in chunks of 2MB
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();
    let cursor = 0; // current position in file

    fileReader.onerror = () =>
      reject("MD5 calculation failed - error reading the file");
    fileReader.onload = (e) => {
      spark.append(e.target.result); // Append array buffer
      cursor += chunkSize; // Move to next chunk

      if (cursor < file.size) {
        fileReader.readAsArrayBuffer(file.slice(cursor, cursor + chunkSize));
      } else {
        resolve(spark.end()); // Complete
      }
    };

    fileReader.readAsArrayBuffer(file.slice(0, chunkSize)); // Start reading
  });
}

async function deleteBlobFromAzure(uploadUrl) {
  try {
    // Send DELETE request to the Azure Blob Storage
    const response = await fetch(uploadUrl, {
      method: "DELETE",
      headers: {
        "x-ms-blob-type": "BlockBlob",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete blob: ${response.statusText}`);
    }
  } catch (error) {
    console.error("Error deleting blob:", error);
  }
}
